import { gsap } from 'gsap'
import { FC, useCallback, useRef } from 'react'
import {
  ClearNotificationArg,
  NotificationArg,
  NotificationCard,
} from './NotificationCard'
import { v4 as uuidv4 } from 'uuid'

const timeoutDelay = 6500
const slideDuration = 0.8
const growDuration = 0.4
const expendingDuration = 0.2

interface IProps {
  notificationsList: ClearNotificationArg[]
  clearNotification(id: string): void
}

export const notificationMessageConstructor = (
  message: NotificationArg['message'],
  link: NotificationArg['link'],
  type: NotificationArg['type']
) => ({
  message,
  link,
  type,
  id: uuidv4(),
})

export const NotificationFactory: FC<IProps> = (props) => {
  // --------------------- ===
  //  PROPS
  // ---------------------
  const { notificationsList, clearNotification } = props

  // --------------------- ===
  //  REFS
  // ---------------------
  const wrapperRef = useRef<HTMLDivElement>(null)

  // --------------------- ===
  //  HELPERS
  // ---------------------
  const getChildrenArr = () => {
    if (wrapperRef.current) {
      const { children } = wrapperRef.current
      return Array.from(children)
    }
  }

  // --------------------- ===
  //  HANDLERS
  // ---------------------
  const handleHideNotification = useCallback((id: string) => {
    const arr = getChildrenArr()
    // Fade out
    arr?.forEach((child) => {
      if (child.id === id) {
        // Animate it out
        gsap.to(child, {
          duration: slideDuration,
          delay: expendingDuration,
          yPercent: 200,
          opacity: 0,
          ease: 'elastic.inOut(2.5, 1)',
          overwrite: false,
        })
        gsap.to(child, {
          duration: growDuration,
          delay: slideDuration,
          height: 0,
          margin: 0,
          padding: 0,
          overwrite: false,
          onComplete: () => {
            clearNotification(id)
          },
        })
      }
    })
  }, [])

  // --------------------- ===
  //  RENDER
  // ---------------------
  return (
    <div
      className="w-[364px] z-50 fixed bottom-0 right-4 0translate-x-1/2 z-notifications max-w-full flex flex-col items-center"
      id="notificationsWrapper" // DO NOT REMOVE - used by ModalWrapper
      ref={wrapperRef}
    >
      {notificationsList.map((notification: ClearNotificationArg) => (
        <NotificationCard
          key={notification.id}
          notification={notification}
          duration={growDuration}
          slideDuration={slideDuration}
          expendingDuration={expendingDuration}
          timeoutDelay={timeoutDelay / 1000} // ms to s
          onClose={() => handleHideNotification(notification.id)}
        />
      ))}
    </div>
  )
}
